import { useBusinessSettings } from "@/hooks/useBusinessSettings";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useDemo } from "@/contexts/DemoContext";
import { getCustomerPlanLabel, getDemoPlanById, normalizePlanId } from "@/lib/planCatalog";

interface PlanFeatures {
  whatsapp?: boolean;
  crm_integrations?: boolean;
  data_export?: boolean;
  push_notifications?: boolean;
  advanced_reports?: boolean;
  api_access?: boolean;
  custom_branding?: boolean;
  inspections?: boolean;
  multi_location?: boolean;
  public_portal?: boolean;
  loyalty?: boolean;
  addons_catalog?: boolean;
  online_payment?: boolean;
  leasing?: boolean;
  delivery_service?: boolean;
  insurance_claims?: boolean;
  email_notifications?: boolean;
  vehicle_compare?: boolean;
  ai_assistant?: boolean;
  [key: string]: boolean | undefined;
}

interface Entitlements {
  isLoading: boolean;
  vehicleLimit: number;
  userLimit: number;
  branchLimit: number;
  vehiclesUsed: number;
  usersUsed: number;
  branchesUsed: number;
  canAddVehicle: boolean;
  canAddUser: boolean;
  canAddBranch: boolean;
  isAtVehicleLimit: boolean;
  isAtUserLimit: boolean;
  isAtBranchLimit: boolean;
  features: PlanFeatures;
  hasFeature: (key: string) => boolean;
  planName: string;
  whatsappAddonEnabled: boolean;
  aiAddonEnabled: boolean;
}

type BusinessAddonFlags = {
  whatsapp_addon_enabled?: boolean | null;
  ai_addon_enabled?: boolean | null;
};

type CountQuery = PromiseLike<{ count: number | null; error: Error | null }> & {
  eq: (column: string, value: string) => CountQuery;
};
type UntypedSupabase = {
  from: (table: string) => {
    select: (columns: string, options: { count: "exact"; head: true }) => CountQuery;
  };
};
const untypedSupabase = supabase as unknown as UntypedSupabase;

export function useEntitlements(): Entitlements {
  const { business, isLoading: bizLoading } = useBusinessSettings();
  const { isDemo } = useDemo();

  const currentPlanId = normalizePlanId(business?.plan_id);
  const fallbackPlan = getDemoPlanById(currentPlanId);

  const { data: plan, isLoading: planLoading } = useQuery({
    queryKey: ["plan-details", currentPlanId],
    queryFn: async () => {
      if (isDemo) return fallbackPlan;

      const { data, error } = await supabase
        .from("plans")
        .select("*")
        .eq("id", currentPlanId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });

  const { data: vehiclesUsed = 0 } = useQuery({
    queryKey: ["vehicle-count", business?.id],
    enabled: !isDemo && !!business?.id,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("vehicles")
        .select("*", { count: "exact", head: true })
        .eq("business_id", business!.id);

      if (error) throw error;
      return count ?? 0;
    },
  });

  const { data: usersUsed = 0 } = useQuery({
    queryKey: ["user-count", business?.id],
    enabled: !isDemo && !!business?.id,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .eq("business_id", business!.id);

      if (error) throw error;
      return count ?? 0;
    },
  });

  const { data: branchesUsed = 0 } = useQuery({
    queryKey: ["branch-count", business?.id],
    enabled: !isDemo && !!business?.id,
    queryFn: async () => {
      const { count, error } = await untypedSupabase
        .from("branches")
        .select("*", { count: "exact", head: true })
        .eq("business_id", business!.id);

      if (error) throw error;
      return count ?? 0;
    },
  });

  const vehicleLimit = plan?.max_vehicles ?? fallbackPlan.max_vehicles ?? 10;
  const userLimit = plan?.max_users ?? fallbackPlan.max_users ?? 1;
  const branchLimit = plan?.max_branches ?? fallbackPlan.max_branches ?? 1;
  const features = (plan?.features as PlanFeatures) ?? (fallbackPlan.features as PlanFeatures);
  const businessAddonFlags = business as (typeof business & BusinessAddonFlags) | undefined;

  const whatsappAddonEnabled = !!businessAddonFlags?.whatsapp_addon_enabled;
  const aiAddonEnabled = !!businessAddonFlags?.ai_addon_enabled;

  const hasFeature = (key: string): boolean => {
    if (isDemo) return true;
    if (key === "whatsapp") return features.whatsapp === true || whatsappAddonEnabled;
    if (key === "ai_assistant") return features.ai_assistant === true || aiAddonEnabled;
    return features[key] === true;
  };

  return {
    isLoading: bizLoading || planLoading,
    vehicleLimit,
    userLimit,
    branchLimit,
    vehiclesUsed,
    usersUsed,
    branchesUsed: isDemo ? 3 : branchesUsed,
    canAddVehicle: isDemo || vehiclesUsed < vehicleLimit,
    canAddUser: isDemo || usersUsed < userLimit,
    canAddBranch: isDemo || branchesUsed < branchLimit,
    isAtVehicleLimit: !isDemo && vehiclesUsed >= vehicleLimit,
    isAtUserLimit: !isDemo && usersUsed >= userLimit,
    isAtBranchLimit: !isDemo && branchesUsed >= branchLimit,
    features,
    hasFeature,
    planName: getCustomerPlanLabel(plan ?? { id: currentPlanId, name: fallbackPlan.name }),
    whatsappAddonEnabled,
    aiAddonEnabled,
  };
}
